import React, { useState } from "react";
import { Link } from "react-scroll";
import { scrollDuration } from "../../../config/commonConfig";
import { Tooltip } from "../../Tooltip";
import XIcon from "../../icons/XIcon";
import LanguageSelector from "../../LanguageSelector";
import T from "../../Translate/T";
import { useTheme } from "../../../context/ThemeContext";
import SignatureLogo from "../../SignatureLogo";

const StandardMenuHeader = () => {
  const [isNavModalClose, setIsNavModalClose] = useState(true);
  const { isDark, toggleTheme } = useTheme();

  const closeNav = (e) => {
    e.preventDefault();
    setIsNavModalClose(true);
  };

  return (
    <header id="header" className="sticky-top" role="banner">
      {/* Navbar */}
      <nav
        className="primary-menu navbar navbar-expand-lg text-uppercase navbar-line-under-text fw-600"
        aria-label="Main navigation"
      >
        <div className="container position-relative">

          {/* ── Logo ── */}
          <div className="col-auto col-lg-2 d-inline-flex ps-lg-0">
            <Link
              className="logo"
              title="Carlos Ramirez — Home"
              aria-label="Carlos Ramirez — go to home section"
              smooth="easeInOutQuint"
              duration={scrollDuration}
              style={{ cursor: "pointer" }}
              offset={-72}
              to="home"
              onClick={closeNav}
            >
              <SignatureLogo />
            </Link>
          </div>

          {/* ── Nav links ── */}
          <div className="col col-lg-8 navbar-accordion px-0">
            <button
              className={
                "navbar-toggler ms-auto collapsed " +
                (isNavModalClose ? "" : "show")
              }
              type="button"
              aria-expanded={!isNavModalClose}
              aria-controls="header-nav"
              aria-label={
                isNavModalClose ? "Open navigation menu" : "Close navigation menu"
              }
              onClick={() => setIsNavModalClose(!isNavModalClose)}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>

            <div
              id="header-nav"
              className={
                "collapse navbar-collapse justify-content-center " +
                (isNavModalClose ? "" : "show")
              }
            >
              <ul className="navbar-nav" role="list">

                <li className="nav-item" role="listitem">
                  <Link
                    className="nav-link"
                    smooth="easeInOutQuint"
                    duration={scrollDuration}
                    style={{ cursor: "pointer" }}
                    activeClass="active"
                    aria-label="Go to Home section"
                    spy
                    to="home"
                    offset={-71}
                    onClick={closeNav}
                  >
                    <T>Home</T>
                  </Link>
                </li>

                <li className="nav-item" role="listitem">
                  <Link
                    className="nav-link"
                    smooth="easeInOutQuint"
                    duration={scrollDuration}
                    style={{ cursor: "pointer" }}
                    activeClass="active"
                    aria-label="Go to About section"
                    spy
                    to="about"
                    onClick={closeNav}
                  >
                    <T>About</T>
                  </Link>
                </li>

                <li className="nav-item" role="listitem">
                  <Link
                    className="nav-link"
                    smooth="easeInOutQuint"
                    duration={scrollDuration}
                    style={{ cursor: "pointer" }}
                    activeClass="active"
                    aria-label="Go to Services section"
                    spy
                    to="services"
                    onClick={closeNav}
                  >
                    <T>Services</T>
                  </Link>
                </li>

                <li className="nav-item" role="listitem">
                  <Link
                    className="nav-link"
                    smooth="easeInOutQuint"
                    duration={scrollDuration}
                    style={{ cursor: "pointer" }}
                    activeClass="active"
                    aria-label="Go to Portfolio section"
                    spy
                    to="portfolio"
                    onClick={closeNav}
                  >
                    <T>Portfolio</T>
                  </Link>
                </li>

                <li className="nav-item" role="listitem">
                  <Link
                    className="nav-link"
                    smooth="easeInOutQuint"
                    duration={scrollDuration}
                    style={{ cursor: "pointer" }}
                    activeClass="active"
                    aria-label="Go to Contact section"
                    spy
                    to="contact"
                    onClick={closeNav}
                  >
                    <T>Contact</T>
                  </Link>
                </li>

              </ul>
            </div>
          </div>

          {/* ── Right side: social + language + theme toggle ── */}
          <div
            className="col-auto d-flex justify-content-end align-items-center ps-0"
            style={{ flexShrink: 0 }}
          >
            <ul
              className="social-icons"
              role="list"
              aria-label="Social media links"
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                marginBottom: 0,
              }}
            >
              <li className="social-icons-x" role="listitem" style={{ display: "inline-flex" }}>
                <Tooltip text="X (Twitter)" placement="bottom">
                  <a
                    href="https://x.com/eramirezcarlos"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Carlos Ramirez on X / Twitter (opens in new tab)"
                  >
                    <XIcon size={16} aria-hidden="true" />
                  </a>
                </Tooltip>
              </li>

              <li className="social-icons-linkedin" role="listitem" style={{ display: "inline-flex" }}>
                <Tooltip text="LinkedIn" placement="bottom">
                  <a
                    href="https://www.linkedin.com/in/carlos-a-ramirez-2800281a8/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Carlos Ramirez on LinkedIn (opens in new tab)"
                  >
                    <i className="fab fa-linkedin" aria-hidden="true" />
                  </a>
                </Tooltip>
              </li>

              <li className="social-icons-github" role="listitem" style={{ display: "inline-flex" }}>
                <Tooltip text="GitHub" placement="top">
                  <a
                    href="https://github.com/eramirezcarlos"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Carlos Ramirez on GitHub (opens in new tab)"
                  >
                    <i className="fab fa-github" aria-hidden="true" />
                  </a>
                </Tooltip>
              </li>
            </ul>

            {/* Language selector */}
            <LanguageSelector />

            {/* Dark / Light theme toggle */}
            <Tooltip
              text={isDark ? "Switch to light mode" : "Switch to dark mode"}
              placement="bottom"
            >
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                aria-pressed={isDark}
              >
                {isDark ? (
                  <i className="fas fa-sun" aria-hidden="true" />
                ) : (
                  <i className="fas fa-moon" aria-hidden="true" />
                )}
              </button>
            </Tooltip>
          </div>

        </div>
      </nav>
      {/* Navbar End */}
    </header>
  );
};

export default StandardMenuHeader;
